/**
 * TextField / SelectField — labelled inputs with a real `<label htmlFor>`, errors wired via
 * `aria-describedby` + `aria-invalid`, and a ≥44px control height.
 */
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '@/lib/utils';

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
  inputId: string;
  children: (ids: { hintId: string; errorId: string; describedBy?: string }) => ReactNode;
}

function FieldShell({ label, hint, error, inputId, children }: FieldShellProps) {
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      <label
        htmlFor={inputId}
        className="mb-1.5 block font-display text-[12px] font-medium text-ink-quiet"
      >
        {label}
      </label>
      {children({ hintId, errorId, describedBy })}
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-[11.5px] leading-snug text-ink-quiet">
          {hint}
        </p>
      )}
      {/* `ink` words behind a coral rule, not coral words: coral-deep is 4.03:1 on paper and so
          fails AA at this size, which would make the validation message the hardest text on the
          form to read. The rule + role="alert" + the field's own coral border carry "problem". */}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1.5 border-l-2 border-coral-deep pl-2 text-[11.5px] font-medium leading-snug text-ink"
        >
          {error}
        </p>
      )}
    </div>
  );
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, id, className, ...props },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;

  return (
    <FieldShell label={label} hint={hint} error={error} inputId={inputId}>
      {({ describedBy }) => (
        <input
          id={inputId}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'min-h-[44px] w-full rounded-md border bg-surface px-3.5 py-2 font-body text-[14px] font-medium text-ink outline-none transition-colors duration-ui ease-thread',
            'placeholder:font-normal placeholder:text-ink-quiet',
            'focus:border-sage focus:ring-2 focus:ring-sage-deep/20',
            error ? 'border-coral-deep' : 'border-line-2',
            className,
          )}
          {...props}
        />
      )}
    </FieldShell>
  );
});

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, hint, error, id, className, children, ...props },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;

  return (
    <FieldShell label={label} hint={hint} error={error} inputId={inputId}>
      {({ describedBy }) => (
        <select
          id={inputId}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'min-h-[44px] w-full rounded-md border border-line-2 bg-surface px-3 py-2 font-body text-[14px] font-medium text-ink outline-none transition-colors duration-ui ease-thread',
            'focus:border-sage focus:ring-2 focus:ring-sage-deep/20',
            className,
          )}
          {...props}
        >
          {children}
        </select>
      )}
    </FieldShell>
  );
});
